import React, { useEffect, useState } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { useSelector } from "react-redux";
import { GetAdminAnalyticsLineChart } from "../../../../apiServices";
import { debounce } from "lodash";
import { Dateyear, MonthDate } from "../../../../utility";
import styels from "../../PieChart.module.scss";

ChartJS.register(
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Title,
  Tooltip,
  Legend
);

const AdminLineChart = () => {
  const LineFilter = useSelector(
    (state) => state.CandidateDashboardFilterDetails.CandidateLineFilter
  );
  console.log(LineFilter.countType);

  const [chartData, setChartData] = useState({
    labels: [],
    datasets: [
      {
        label: "Joined",
        data: [],
        borderColor: "#3d85c6",
        backgroundColor: "#3d86c63e",
        pointBackgroundColor: "#3d85c6",
        // "", "#E65122", "#169c50"
        tension: 0.1,
        // hidden: LineFilter.countType !== "Joined",
      },
      {
        label: "Left Company",
        data: [],
        borderColor: "#E65122",
        backgroundColor: "#e6502263",
        pointBackgroundColor: "#E65122",
        tension: 0.1,
        hidden: true,
      },
      {
        label: "Currently Working",
        data: [],
        borderColor: "#169c50",
        backgroundColor: "#169c5054",
        pointBackgroundColor: "#169c50",
        tension: 0.1,
        hidden: true,
      },
    ],
  });
  // console.log(Dateyear("2024-05-05"));

  useEffect(() => {
    const fetchData = async () => {
      try {
        const getData = await GetAdminAnalyticsLineChart({ ...LineFilter });

        // Calculate totals for either weeks or months based on LineFilter.type
        const aggregatedData = getData.data.reduce((acc, entry) => {
          const {
            week_start,
            week_end,
            month_start,
            month_end,
            joined_count,
            left_count,
            currently_working,
          } = entry;

          let periodKey = "";
          if (LineFilter.type === "weekly") {
            // Use week range for weekly data
            periodKey = `${MonthDate(week_start)} - ${Dateyear(week_end)}`;
          } else if (LineFilter.type === "monthly") {
            // Use month range for monthly data
            periodKey = `${MonthDate(month_start)} - ${Dateyear(month_end)}`;
          }

          // Initialize the entry if it doesn't exist
          if (!acc[periodKey]) {
            acc[periodKey] = {
              joined_count: 0,
              left_count: 0,
              currently_working: 0,
            };
          }

          // Sum up the counts for the selected period (week or month)
          acc[periodKey].joined_count += parseInt(joined_count, 10);
          acc[periodKey].left_count += parseInt(left_count, 10);
          acc[periodKey].currently_working += parseInt(currently_working, 10);

          return acc;
        }, {});

        // Transform aggregated data into arrays for Chart.js
        const labels = Object.keys(aggregatedData);
        const joinedData = labels.map(
          (label) => aggregatedData[label].joined_count
        );
        const leftData = labels.map(
          (label) => aggregatedData[label].left_count
        );
        const currentlyWorkingData = labels.map(
          (label) => aggregatedData[label].currently_working
        );

        // Update chart data state
        setChartData({
          labels,
          datasets: [
            { ...chartData.datasets[0], data: joinedData },
            { ...chartData.datasets[1], data: leftData },
            { ...chartData.datasets[2], data: currentlyWorkingData },
          ],
        });
      } catch (error) {
        console.error("Error fetching chart data:", error);
      }
    };

    const debouncedFetchData = debounce(fetchData, 300);
    debouncedFetchData();

    return () => {
      debouncedFetchData.cancel();
    };
  }, [LineFilter]);

  // const options = {
  //   responsive: true,
  //   maintainAspectRatio: false,
  //   plugins: {
  //     legend: {
  //       position: "top",
  //       labels: {
  //         usePointStyle: true,
  //         padding: 10,
  //       },

  //       onHover: function (event) {
  //         event.native.target.style.cursor = "pointer";
  //       },
  //     },
  //     tooltip: { mode: "index", intersect: false },
  //     datalabels: {
  //       display: false, // Disable data labels that show numbers inside points
  //     },
  //   },
  //   layout: {
  //     padding: {
  //       bottom: 10,
  //     },
  //   },
  //   scales: {
  //     x: {
  //       display: true,
  //       title: {
  //         display: true,
  //         text: LineFilter.type === "weekly" ? "Weeks" : "Months",
  //       },
  //       grid: {
  //         display: false, // Disable grid lines on the y-axis
  //       },
  //     },
  //     y: {
  //       display: true,
  //       title: { display: true, text: "Values" },
  //       ticks: {
  //         callback: function (value) {
  //           // Display only integer values
  //           return Number.isInteger(value) ? value : null;
  //         },

  //         // stepSize: 5,
  //       },
  //     },
  //   },
  //   // elements: {
  //   //   point: {
  //   //     radius: 1, // This removes the circular markers at each data point
  //   //     hoverRadius: 0, // This removes the hover effect on the data points as well
  //   //   },
  //   // },
  // };
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
        labels: {
          usePointStyle: true,
        },
        onHover: function (event) {
          event.native.target.style.cursor = "pointer";
        },
      },
      tooltip: {
        mode: "index",
        intersect: false,
      },
      datalabels: {
        display: false,
      },
    },

    scales: {
      x: {
        display: true,
        title: {
          display: true,
          text: LineFilter.type === "weekly" ? "Weeks" : "Months",
        },

        grid: {
          display: false,
        },
      },
      y: {
        display: true,
        title: { display: true, text: "Values" },
        ticks: {
          beginAtZero: true,
          // stepSize: 2,
          max: 100, // Set max value as per your data range
          min: 0, // Set min value to 0 or the minimum value you'd like
          callback: function (value) {
            return Number.isInteger(value) ? value : null;
          },
        },
        // ticks: {
        //   callback: function (value) {
        //     return Number.isInteger(value) ? value : null;
        //   },
        // },
      },
    },
  };

  return (
    <div className="d-flex align-items-center justify-content-center mt-5">
      <div className={styels.lineChartLayout}>
        <Line data={chartData} options={options} />
      </div>
    </div>
  );
};

export default AdminLineChart;
